import React from "react";

/* ================================================================
   Cosmic & Universe — 8 animated templates
   Black hole, nebula, supernova, comet, big bang, exoplanet, pulsar, dark matter
   Deep space visuals with particle and rotation animations
   ================================================================ */

// ── 1. Black Hole ────────────────────────────────────────────────────
export const BlackHole: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <defs>
        <radialGradient id="bh-core"><stop offset="0%" stopColor="#000" /><stop offset="60%" stopColor="#0f172a" /><stop offset="100%" stopColor="#1e1b4b" stopOpacity="0" /></radialGradient>
        <radialGradient id="bh-disk"><stop offset="0%" stopColor="#f59e0b" stopOpacity="0.8" /><stop offset="50%" stopColor="#ef4444" stopOpacity="0.4" /><stop offset="100%" stopColor="#7c3aed" stopOpacity="0" /></radialGradient>
      </defs>
      {/* Stars */}
      {Array.from({ length: 40 }).map((_, i) => (
        <circle key={i} cx={20 + (i * 97) % 760} cy={10 + (i * 53) % 480} r={0.5 + (i % 3) * 0.5} fill="#fff" opacity={0.3 + (i % 5) * 0.12}>
          <animate attributeName="opacity" values={`${0.2};${0.8};${0.2}`} dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Accretion disk */}
      <g transform={`rotate(${rotation * 0.3},400,250)`}>
        <ellipse cx="400" cy="250" rx="180" ry="50" fill="none" stroke="#f59e0b" strokeWidth="35" opacity="0.3" />
        <ellipse cx="400" cy="250" rx="160" ry="42" fill="none" stroke="#ef4444" strokeWidth="20" opacity="0.4" />
        <ellipse cx="400" cy="250" rx="140" ry="35" fill="none" stroke="#fbbf24" strokeWidth="8" opacity="0.6">
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.5s" repeatCount="indefinite" />
        </ellipse>
      </g>
      {/* Event horizon */}
      <circle cx="400" cy="250" r="45" fill="url(#bh-core)" />
      <circle cx="400" cy="250" r="30" fill="#000" />
      {/* Gravitational lensing ring */}
      <circle cx="400" cy="250" r="50" fill="none" stroke="#e0e7ff" strokeWidth="1.5" opacity="0.3">
        <animate attributeName="r" values="48;55;48" dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Jets */}
      <line x1="400" y1="200" x2="400" y2="30" stroke="#818cf8" strokeWidth="4" opacity="0.4">
        <animate attributeName="opacity" values="0.2;0.6;0.2" dur="1s" repeatCount="indefinite" />
      </line>
      <line x1="400" y1="300" x2="400" y2="470" stroke="#818cf8" strokeWidth="4" opacity="0.4">
        <animate attributeName="opacity" values="0.2;0.6;0.2" dur="1s" repeatCount="indefinite" />
      </line>
      <text x="400" y="490" textAnchor="middle" fill="#a78bfa" fontSize="16" fontWeight="bold">Black Hole</text>
    </svg>
  );
};

// ── 2. Nebula Cloud ──────────────────────────────────────────────────
export const NebulaCloud: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const drift = Math.sin(phase * 0.3) * 10;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Background stars */}
      {Array.from({ length: 30 }).map((_, i) => (
        <circle key={i} cx={15 + (i * 79) % 770} cy={10 + (i * 47) % 480} r={0.8} fill="#fff" opacity={0.4}>
          <animate attributeName="opacity" values="0.2;0.7;0.2" dur={`${3 + i % 3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Nebula layers */}
      <ellipse cx={380 + drift} cy={240} rx="200" ry="120" fill="#7c3aed" opacity="0.15">
        <animate attributeName="rx" values="190;210;190" dur="8s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx={420 - drift * 0.5} cy={250} rx="160" ry="100" fill="#ec4899" opacity="0.12">
        <animate attributeName="ry" values="95;110;95" dur="6s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx={400 + drift * 0.3} cy={230} rx="120" ry="80" fill="#3b82f6" opacity="0.18">
        <animate attributeName="rx" values="115;130;115" dur="7s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx={390} cy={260} rx="80" ry="50" fill="#f472b6" opacity="0.2">
        <animate attributeName="opacity" values="0.15;0.25;0.15" dur="5s" repeatCount="indefinite" />
      </ellipse>
      {/* Embedded stars forming */}
      {[{ x: 350, y: 220 }, { x: 430, y: 260 }, { x: 400, y: 200 }, { x: 370, y: 270 }].map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r="3" fill="#fff">
          <animate attributeName="r" values="1;4;1" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;1;0.5" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <text x="400" y="430" textAnchor="middle" fill="#c4b5fd" fontSize="16" fontWeight="bold">Nebula — Stellar Nursery</text>
    </svg>
  );
};

// ── 3. Supernova Explosion ───────────────────────────────────────────
export const SupernovaExplosion: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const expandR = 20 + animationProgress * 180;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Shockwave rings */}
      <circle cx="400" cy="250" r={expandR} fill="none" stroke="#ef4444" strokeWidth="3" opacity={1 - animationProgress * 0.7}>
        <animate attributeName="stroke-width" values="6;1" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="250" r={expandR * 0.7} fill="none" stroke="#f59e0b" strokeWidth="2" opacity={1 - animationProgress * 0.5} />
      <circle cx="400" cy="250" r={expandR * 0.4} fill="none" stroke="#fbbf24" strokeWidth="4" opacity={1 - animationProgress * 0.3} />
      {/* Core */}
      <circle cx="400" cy="250" r={15 - animationProgress * 10} fill="#fff">
        <animate attributeName="opacity" values="1;0.5;1" dur="0.3s" repeatCount="indefinite" />
      </circle>
      {/* Ejected particles */}
      {Array.from({ length: 20 }).map((_, i) => {
        const angle = (i / 20) * Math.PI * 2;
        const dist = animationProgress * (120 + (i % 5) * 30);
        return (
          <circle key={i} cx={400 + Math.cos(angle) * dist} cy={250 + Math.sin(angle) * dist} r={3 - animationProgress * 2} fill={i % 3 === 0 ? "#ef4444" : i % 3 === 1 ? "#f59e0b" : "#3b82f6"} opacity={1 - animationProgress}>
            <animate attributeName="opacity" values={`${1 - animationProgress};0`} dur="2s" repeatCount="indefinite" />
          </circle>
        );
      })}
      <text x="400" y="470" textAnchor="middle" fill="#ef4444" fontSize="18" fontWeight="bold">Supernova</text>
    </svg>
  );
};

// ── 4. Comet Trail ───────────────────────────────────────────────────
export const CometTrail: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const cx = 100 + animationProgress * 600;
  const cy = 380 - Math.sin(animationProgress * Math.PI) * 250;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Stars */}
      {Array.from({ length: 25 }).map((_, i) => (
        <circle key={i} cx={(i * 83) % 800} cy={(i * 49) % 500} r="1" fill="#fff" opacity="0.5" />
      ))}
      {/* Tail */}
      <path d={`M${cx},${cy} Q${cx - 80},${cy + 20} ${cx - 200},${cy + 50} Q${cx - 250},${cy + 55} ${cx - 300},${cy + 40}`} stroke="#60a5fa" strokeWidth="8" fill="none" opacity="0.3" />
      <path d={`M${cx},${cy} Q${cx - 60},${cy + 10} ${cx - 150},${cy + 30}`} stroke="#93c5fd" strokeWidth="4" fill="none" opacity="0.5" />
      {/* Dust tail */}
      <path d={`M${cx},${cy} Q${cx - 70},${cy + 40} ${cx - 180},${cy + 80}`} stroke="#fbbf24" strokeWidth="6" fill="none" opacity="0.2" />
      {/* Comet head */}
      <circle cx={cx} cy={cy} r="12" fill="#e0f2fe" />
      <circle cx={cx} cy={cy} r="8" fill="#fff">
        <animate attributeName="r" values="6;10;6" dur="1s" repeatCount="indefinite" />
      </circle>
      {/* Coma glow */}
      <circle cx={cx} cy={cy} r="25" fill="#93c5fd" opacity="0.15">
        <animate attributeName="r" values="20;30;20" dur="1.5s" repeatCount="indefinite" />
      </circle>
      <text x="400" y="480" textAnchor="middle" fill="#93c5fd" fontSize="16" fontWeight="bold">Comet</text>
    </svg>
  );
};

// ── 5. Big Bang ──────────────────────────────────────────────────────
export const BigBang: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const era = animationProgress < 0.2 ? "singularity" : animationProgress < 0.5 ? "inflation" : animationProgress < 0.8 ? "particle" : "matter";
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Expanding universe */}
      <circle cx="400" cy="250" r={animationProgress * 200} fill="none" stroke="#f59e0b" strokeWidth="2" opacity={0.3}>
        <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="250" r={animationProgress * 150} fill="none" stroke="#ef4444" strokeWidth="3" opacity="0.4" />
      <circle cx="400" cy="250" r={animationProgress * 100} fill="none" stroke="#fbbf24" strokeWidth="4" opacity="0.5" />
      {/* Core flash */}
      <circle cx="400" cy="250" r={10 + (1 - animationProgress) * 30} fill="#fff" opacity={1 - animationProgress * 0.7}>
        <animate attributeName="opacity" values="0.8;1;0.8" dur="0.5s" repeatCount="indefinite" />
      </circle>
      {/* Particles forming */}
      {animationProgress > 0.3 && Array.from({ length: 15 }).map((_, i) => {
        const angle = (i / 15) * Math.PI * 2;
        const dist = (animationProgress - 0.3) * 250;
        return (
          <circle key={i} cx={400 + Math.cos(angle) * dist} cy={250 + Math.sin(angle) * dist} r="3" fill={i % 3 === 0 ? "#ef4444" : i % 3 === 1 ? "#3b82f6" : "#10b981"} opacity={0.8}>
            <animate attributeName="r" values="2;4;2" dur={`${1 + i * 0.2}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {/* Galaxy formations */}
      {animationProgress > 0.7 && [
        { x: 250, y: 180 }, { x: 550, y: 320 }, { x: 350, y: 350 }, { x: 500, y: 150 }
      ].map((g, i) => (
        <g key={i}>
          <ellipse cx={g.x} cy={g.y} rx="20" ry="8" fill="#a78bfa" opacity="0.4" transform={`rotate(${i * 30},${g.x},${g.y})`}>
            <animateTransform attributeName="transform" type="rotate" values={`${i * 30},${g.x},${g.y};${i * 30 + 360},${g.x},${g.y}`} dur="10s" repeatCount="indefinite" />
          </ellipse>
        </g>
      ))}
      {/* Timeline label */}
      <rect x="150" y="440" width="500" height="4" rx="2" fill="#334155" />
      <circle cx={150 + animationProgress * 500} cy="442" r="6" fill="#fbbf24" />
      <text x={150 + animationProgress * 500} y="470" textAnchor="middle" fill="#94a3b8" fontSize="11">{era}</text>
      <text x="400" y="30" textAnchor="middle" fill="#fbbf24" fontSize="18" fontWeight="bold">The Big Bang</text>
    </svg>
  );
};

// ── 6. Exoplanet Transit ─────────────────────────────────────────────
export const ExoplanetTransit: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const planetX = 150 + animationProgress * 500;
  const isTransiting = planetX > 330 && planetX < 470;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Star */}
      <circle cx="400" cy="220" r="80" fill="#fbbf24" opacity={isTransiting ? 0.7 : 1}>
        <animate attributeName="r" values="78;82;78" dur="3s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="220" r="85" fill="#fbbf24" opacity="0.1">
        <animate attributeName="r" values="85;95;85" dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Planet transiting */}
      <circle cx={planetX} cy="220" r="20" fill="#1e293b" />
      <circle cx={planetX + 3} cy="218" r="5" fill="#334155" opacity="0.5" />
      {/* Light curve */}
      <polyline points={`100,400 ${Math.min(planetX - 30, 330)},400 ${isTransiting ? `${planetX},430` : `${planetX},400`} ${Math.max(planetX + 30, 470)},400 700,400`} fill="none" stroke="#60a5fa" strokeWidth="2" />
      <rect x="100" y="380" width="600" height="60" fill="none" stroke="#334155" strokeWidth="1" />
      <text x="80" y="405" fill="#64748b" fontSize="10" textAnchor="end">Brightness</text>
      <text x="400" y="460" textAnchor="middle" fill="#64748b" fontSize="10">Time →</text>
      <text x="400" y="490" textAnchor="middle" fill="#60a5fa" fontSize="14" fontWeight="bold">Exoplanet Transit Method</text>
    </svg>
  );
};

// ── 7. Pulsar Spin ───────────────────────────────────────────────────
export const PulsarSpin: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Stars background */}
      {Array.from({ length: 20 }).map((_, i) => (
        <circle key={i} cx={(i * 91) % 800} cy={(i * 61) % 500} r="1" fill="#fff" opacity="0.3" />
      ))}
      {/* Neutron star */}
      <circle cx="400" cy="250" r="25" fill="#a5b4fc" stroke="#818cf8" strokeWidth="2">
        <animate attributeName="r" values="23;27;23" dur="0.1s" repeatCount="indefinite" />
      </circle>
      {/* Magnetic field lines */}
      <g transform={`rotate(${rotation},400,250)`}>
        <ellipse cx="400" cy="250" rx="100" ry="40" fill="none" stroke="#6366f1" strokeWidth="1" opacity="0.3" strokeDasharray="4,4" />
        <ellipse cx="400" cy="250" rx="70" ry="30" fill="none" stroke="#818cf8" strokeWidth="1" opacity="0.4" strokeDasharray="4,4" />
      </g>
      {/* Beams */}
      <g transform={`rotate(${rotation},400,250)`}>
        <polygon points="400,225 380,50 420,50" fill="#60a5fa" opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="0.2s" repeatCount="indefinite" />
        </polygon>
        <polygon points="400,275 380,450 420,450" fill="#60a5fa" opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="0.2s" repeatCount="indefinite" />
        </polygon>
      </g>
      {/* Rotation axis */}
      <line x1="400" y1="180" x2="400" y2="320" stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3" opacity="0.4" />
      <text x="400" y="470" textAnchor="middle" fill="#818cf8" fontSize="16" fontWeight="bold">Pulsar</text>
    </svg>
  );
};

// ── 8. Dark Matter Web ───────────────────────────────────────────────
export const DarkMatterWeb: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const nodes = [
    { x: 200, y: 150 }, { x: 400, y: 100 }, { x: 600, y: 160 },
    { x: 150, y: 300 }, { x: 400, y: 280 }, { x: 650, y: 300 },
    { x: 250, y: 420 }, { x: 500, y: 400 }, { x: 700, y: 430 },
  ];
  const edges = [[0,1],[1,2],[0,3],[1,4],[2,5],[3,4],[4,5],[3,6],[4,7],[5,8],[6,7],[7,8],[0,4],[2,4]];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Filaments */}
      {edges.map(([a, b], i) => (
        <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} stroke="#6366f1" strokeWidth="2" opacity="0.2">
          <animate attributeName="opacity" values="0.1;0.3;0.1" dur={`${3 + i * 0.3}s`} repeatCount="indefinite" />
        </line>
      ))}
      {/* Galaxy clusters at nodes */}
      {nodes.map((n, i) => {
        const pulse = Math.sin((phase + i) * 0.5) * 3;
        return (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r={12 + pulse} fill="#7c3aed" opacity="0.15" />
            <circle cx={n.x} cy={n.y} r={6 + pulse * 0.5} fill="#a78bfa" opacity="0.4">
              <animate attributeName="r" values={`${5};${8};${5}`} dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            </circle>
            <circle cx={n.x} cy={n.y} r="2" fill="#e0e7ff" />
          </g>
        );
      })}
      {/* Dark matter halos */}
      {nodes.filter((_, i) => i % 3 === 0).map((n, i) => (
        <circle key={`h${i}`} cx={n.x} cy={n.y} r="30" fill="none" stroke="#4f46e5" strokeWidth="1" strokeDasharray="3,5" opacity="0.2">
          <animate attributeName="r" values="25;35;25" dur="4s" repeatCount="indefinite" />
        </circle>
      ))}
      <text x="400" y="480" textAnchor="middle" fill="#818cf8" fontSize="14" fontWeight="bold">Dark Matter — Cosmic Web</text>
    </svg>
  );
};
